import React, { useMemo, useState } from 'react';
import { Input, Modal, message } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { ze } from 'utils/zhEn';
import SearchBox from 'components/commons/SearchBox';
import scss from './AiDrawer.scss';

/** 按关键词过滤会话标题（不区分大小写） */
export function filterConversationList(conversationList, keyword) {
  const q = (keyword || '').trim().toLowerCase();
  if (!q) return conversationList;
  return conversationList.filter((c) => (c.name || '').toLowerCase().includes(q));
}

const AiChatBotConversationList = ({
  conversationList,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onRenameConversation,
}) => {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  const filteredList = useMemo(
    () => filterConversationList(conversationList, searchKeyword),
    [conversationList, searchKeyword],
  );

  // 时间分组逻辑
  const groupedConversations = useMemo(() => {
    const now = Date.now() / 1000; // 转换为秒
    const oneDay = 24 * 60 * 60;
    const sevenDays = 7 * oneDay;
    const thirtyDays = 30 * oneDay;
    const ninetyDays = 90 * oneDay;

    const groups = {
      today: { title: ze('今天', 'Today'), items: [] },
      yesterday: { title: ze('昨天', 'Yesterday'), items: [] },
      sevenDays: { title: ze('7天内', 'Within 7 days'), items: [] },
      thirtyDays: { title: ze('30天内', 'Within 30 days'), items: [] },
      ninetyDays: { title: ze('90天内', 'Within 90 days'), items: [] },
      older: { title: ze('90天以前', 'More than 90 days ago'), items: [] },
    };

    filteredList.forEach(conversation => {
      const timeDiff = now - conversation.updated_at;

      if (timeDiff < oneDay) {
        groups.today.items.push(conversation);
      } else if (timeDiff < 2 * oneDay) {
        groups.yesterday.items.push(conversation);
      } else if (timeDiff < sevenDays) {
        groups.sevenDays.items.push(conversation);
      } else if (timeDiff < thirtyDays) {
        groups.thirtyDays.items.push(conversation);
      } else if (timeDiff < ninetyDays) {
        groups.ninetyDays.items.push(conversation);
      } else {
        groups.older.items.push(conversation);
      }
    });

    // 只返回有内容的分组
    return Object.values(groups).filter(group => group.items.length > 0);
  }, [filteredList]);

  const handleConversationClick = (conversationId) => {
    onSelectConversation(conversationId);
  };

  const openRenameModal = (conversation, e) => {
    e.stopPropagation();
    setRenameTarget(conversation);
    setRenameValue(conversation.name || '');
  };

  const handleRenameOk = async () => {
    if (!renameTarget || !onRenameConversation) return;
    const nextName = renameValue.trim();
    if (!nextName) {
      message.warning(ze('会话名称不能为空', 'Session name cannot be empty'));
      return;
    }
    setRenaming(true);
    try {
      await onRenameConversation(renameTarget.id, nextName);
      setRenameTarget(null);
      setRenameValue('');
      message.success(ze('已重命名', 'Renamed'));
    } catch (err) {
      message.error(ze('重命名失败，请重试', 'Rename failed, please retry'));
    } finally {
      setRenaming(false);
    }
  };

  return (
    <div className={scss.conversationSidebar}>
      <div className={`${scss.headerTitle} ${scss.conversationSidebarTitle}`}>
        CrashSight AI
      </div>

      <button
        type="button"
        className={scss.conversationSidebarNewBtn}
        onClick={onNewConversation}
      >
        <PlusOutlined />
        {ze('新建会话', 'New Conversation')}
      </button>

      <div className={scss.conversationSidebarSearchWrap}>
        <SearchBox
          placeholder={ze('搜索会话', 'Search conversations')}
          defaultSearch={searchKeyword}
          handleSearchInput={setSearchKeyword}
          style={{
            search_box: scss.conversationSearchBox,
            ui_search: scss.conversationSearchUi,
          }}
        />
      </div>

      <div className={scss.conversationSidebarList}>
        {groupedConversations.length === 0 ? (
          <div className={scss.conversationSidebarEmpty}>
            {searchKeyword.trim()
              ? ze('没有匹配的会话', 'No matching conversations')
              : ze('暂无历史会话', 'No conversation history')}
          </div>
        ) : (
          groupedConversations.map((group, groupIndex) => (
            <div key={groupIndex} className={scss.conversationSidebarGroup}>
              <div className={scss.conversationSidebarGroupTitle}>
                {group.title}
              </div>

              {group.items.map((conversation) => {
                const isSelected = currentConversationId === conversation.id;
                return (
                  <div
                    key={conversation.id}
                    className={`${scss.conversationSidebarItem} ${isSelected ? scss.conversationSidebarItemActive : ''}`}
                    onClick={() => handleConversationClick(conversation.id)}
                    title={conversation.name}
                  >
                    <span className={scss.conversationSidebarItemText}>
                      {conversation.name}
                    </span>
                    {onRenameConversation && (
                      <EditOutlined
                        className={scss.conversationRenameBtn}
                        onClick={(e) => openRenameModal(conversation, e)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      <Modal
        title={ze('重命名会话', 'Rename conversation')}
        open={!!renameTarget}
        confirmLoading={renaming}
        onOk={handleRenameOk}
        onCancel={() => {
          setRenameTarget(null);
          setRenameValue('');
        }}
        destroyOnClose
      >
        <Input
          value={renameValue}
          maxLength={120}
          onChange={(e) => setRenameValue(e.target.value)}
          onPressEnter={handleRenameOk}
          placeholder={ze('输入会话名称', 'Enter session name')}
        />
      </Modal>
    </div>
  );
};

export default AiChatBotConversationList;
